import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "antd"; // Add this import for Tooltip

import showToast from "../../utils/toastNotifications";
import { useThunk } from "../../hooks/useThunk";
import {
  getAllCustomers,
  rejectCustomer,
  suspendCustomer,
  activateCustomer,
  permanentlySuspendCustomer,
} from "../../services/thunks/customerThunks";
import { exportAllSubscribers } from "../../services/thunks/newsletterThunks";

import Table from "../../components/Table/Table";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";

import deleteIcon from "../../assets/icons/delete.svg";
import viewIcon from "../../assets/icons/view.svg";
import searchIcon from "../../assets/icons/search.svg";
import suspendIcon from "../../assets/icons/suspend.svg";
import activeIcon from "../../assets/icons/active.svg";
import ConfirmSuspendModal from "../../components/Modals/ConfirmSuspendModal";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/common/SearchBar";
import { formatJoinedDate } from "../../utils/dateFormating";

const ManageCustomers = () => {
  const auth = useSelector((state) => state.auth);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [doDeleteCustomer, isDeletingCustomer] = useThunk(rejectCustomer);
  const [doSuspendCustomer] = useThunk(suspendCustomer);
  const [doActivateCustomer] = useThunk(activateCustomer);
  const [doPermanentlySuspendCustomer] = useThunk(permanentlySuspendCustomer);
  const [doExportSubscribers] = useThunk(exportAllSubscribers);

  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  const [showPermanentSuspendConfirm, setShowPermanentSuspendConfirm] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const tableRef = useRef(null);
  const pageSetterRef = useRef(null);
  const navigate = useNavigate();

  const handleDelete = async (id) => {
    const result = await doDeleteCustomer({ token: auth.token, id });
    if (result.success) {
      showToast("success", "Customer deleted.");
      setRefreshTrigger((prev) => prev + 1);
    } else {
      showToast("error", result.error.message);
    }
  };

  const handleSuspend = async (id) => {
    const result = await doSuspendCustomer({ token: auth.token, id });
    if (result.success) {
      showToast("success", "Customer suspended successfully.");
      setRefreshTrigger((prev) => prev + 1);
    } else {
      showToast("error", result.error.message);
    }
  };

  const handleActivate = async (id) => {
    const result = await doActivateCustomer({ token: auth.token, id });
    if (result.success) {
      showToast("success", "Customer activated successfully.");
      setRefreshTrigger((prev) => prev + 1);
    } else {
      showToast("error", result.error.message);
    }
  };

  const handlePermanentlySuspend = async (id) => {
    const result = await doPermanentlySuspendCustomer({ token: auth.token, id });
    if (result.success) {
      showToast("success", "Customer permanently suspended and subscription cancelled.");
      setRefreshTrigger((prev) => prev + 1);
    } else {
      showToast("error", result.error.message);
    }
  };

  const handleSearch = () => {
    if (pageSetterRef.current) {
      pageSetterRef.current(1);
    }
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleViewCustomer = (customer) => {
    navigate(`/customer/${customer._id}`);
  };

  const handleExportSubscribers = async () => {
    const result = await doExportSubscribers();
    console.log("Export result:", result);

    if (result.success) {
      const data = result.response;

      if (data instanceof Blob) {
        // Create a download link
        const url = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.download = `subscribers_${
          new Date().toISOString().split("T")[0]
        }.csv`;

        // Trigger download
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showToast("success", "Subscribers exported successfully");
      } else {
        showToast("error", "Invalid file format received");
      }
    } else {
      showToast("error", "No active subscribers found.");
    }
  };

  const renderTextWithTooltip = (text) => {
    if (!text) return "N/A";

    const textElement = (
      <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2] w-full">
        <span className="truncate block ">{text}</span>
      </div>
    );

    return (
      <Tooltip title={text} placement="topLeft">
        {textElement}
      </Tooltip>
    );
  };

  // Render email with tooltip based on width overflow
  const renderEmailWithTooltip = (email) => {
    if (!email) return "N/A";

    const emailElement = (
      <div className="min-h-[32px] flex items-center text-sm font-[400] font-['Public Sans'] text-[#E2E2E2] w-full">
        <span className="truncate block w-full">{email}</span>
      </div>
    );

    return (
      <Tooltip title={email} placement="topLeft">
        {emailElement}
      </Tooltip>
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (tableRef.current) {
        tableRef.current.scrollTo(0, 0);
      }
      if (pageSetterRef.current) {
        pageSetterRef.current(1);
      }
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  const tableColumns = useMemo(
    () => [
      {
        title: <div style={{ paddingLeft: "2.375rem" }}>User ID</div>,
        dataIndex: "userId",
        key: "userId",
        width: "5%",
        render: (text) => (
          <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2] pl-[2.375rem]">
            {text}
          </div>
        ),
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: "15%",
        render: (text) => (
          <div className="max-w-[300px]">
            {" "}
            {/* Set maximum width for Name column */}
            {renderTextWithTooltip(text)} {/* Use tooltip function */}
          </div>
        ),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: "15%",
        render: (text) => (
          <div className="max-w-[280px]">
            {" "}
            {/* Set maximum width for Email column */}
            {renderEmailWithTooltip(text)} {/* Use tooltip function */}
          </div>
        ),
      },
      {
        title: "Subscription",
        dataIndex: "subscription",
        key: "subscription",
        align: "center",
        width: "10%",
        render: (status) => (
          <div
            className={`min-h-[32px] flex items-center text-sm font-[400] font-public-sans ${
              status === "Active" ? "  text-[#07C01D] " : "  text-[#C3C3C3] "
            }`}
          >
            {status || "Inactive"}
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        align: "center",
        width: "10%",
        render: (status) => (
          <div
            className={`min-h-[32px] flex items-center text-sm font-[400] font-public-sans ${
              status === "Active"
                ? "text-[#07C01D]"
                : status === "Suspend"
                  ? "text-[#DC6700]"
                  : "text-[#FF0000]"
            }`}
          >
            {status || "Active"}
          </div>
        ),
      },
      {
        title: "Joined On",
        dataIndex: "joinedOn",
        key: "joinedOn",
        width: "25%",
        render: (date) => {
          const formattedDate = formatJoinedDate(date);

          return (
            <div className="min-h-[32px] min-w-[100px] flex items-center text-sm font-[400] font-['Public Sans'] text-[#E2E2E2]">
              {formattedDate}
            </div>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: "15%",
        render: (_, record) => (
          <div className="flex gap-[8px] justify-start items-center">
            <button
              onClick={() => handleViewCustomer(record)}
              className="flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#07C01D] rounded-[15px] text-[12px] text-[#07C01D] w-[75px]"
            >
              <img src={viewIcon} alt="view" className="w-[10px] h-[11px]" />
              View
            </button>
            <div className={record.status === "Deleted" ? "opacity-0" : "opacity-100"}>
              {record.status === "Active" ? (
                <button
                  onClick={() => {
                    setSelectedCustomer(record);
                    setShowSuspendConfirm(true);
                  }}
                  className="flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#DC6700] rounded-[15px] text-[12px] text-[#DC6700] w-[99px]"
                >
                  <img
                    src={suspendIcon}
                    alt="suspend"
                    className="w-[10px] h-[11px]"
                  />
                  Suspend
                </button>
              ) : record.status === "Suspend" ? (
                <div className="flex gap-[8px]">
                  <button
                    onClick={() => handleActivate(record._id)}
                    className="flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#07C01D] rounded-[15px] text-[12px] text-[#07C01D] w-[99px]"
                  >
                    <img
                      src={activeIcon}
                      alt="activate"
                      className="w-[10px] h-[11px]"
                    />
                    Activate
                  </button>
                  {record.subscription === "Active" && (
                    <button
                      onClick={() => {
                        setSelectedCustomer(record);
                        setShowPermanentSuspendConfirm(true);
                      }}
                      className="flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#C00707] rounded-[15px] text-[12px] text-[#C00707] whitespace-nowrap"
                    >
                      <img
                        src={suspendIcon}
                        alt="permanently suspend"
                        className="w-[10px] h-[11px]"
                      />
                      Permanently Suspend
                    </button>
                  )}
                </div>
              ) : null}
            </div>
            <button
              onClick={() => {
                setSelectedCustomer(record);
                setShowConfirm(true);
              }}
              className="flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#C00707] rounded-[15px] text-[12px] text-[#C00707] w-[65px]"
            >
              <img
                src={deleteIcon}
                alt="delete"
                className="w-[10px] h-[12px]"
              />
              Delete
            </button>
          </div>
        ),
      },
    ],
    [auth.token],
  );

  return (
    <div className="px-6 py-10 text-white min-h-screen">
      {/* Search Section */}
      <div className="flex items-center gap-4 mb-6">
        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setRefreshTrigger={setRefreshTrigger}
          placeholder="Search by name, email, or ID"
          icon={searchIcon}
          containerClass="bg-[#2D2D2D] border border-[#808080] rounded-[5px] px-3 w-full h-[45px]"
          inputClass="bg-transparent outline-none text-sm ml-3 w-full text-[#E2E2E2] font-[400] font-['Public Sans'] pr-6"
          buttonClass="absolute right-3 text-[#A0A0A0] hover:text-white text-lg font-bold"
          onKeyPress={handleKeyPress}
        />
        <button
          onClick={handleSearch}
          className="border border-[#51F9EF] rounded-[3.77px] h-[45px] w-[91px] text-[#51F9EF] text-[11px] uppercase font-['Public Sans']"
        >
          Search
        </button>
        <button
          onClick={handleExportSubscribers}
          className="border border-[#51F9EF] rounded-[3.77px] h-[45px] w-[91px] text-[#51F9EF] text-[11px] uppercase font-['Public Sans']"
        >
          Download Subscribers
        </button>
      </div>

      {/* Table Section */}
      <div ref={tableRef}>
        <Table
          columns={tableColumns}
          fetchData={getAllCustomers}
          refreshTrigger={refreshTrigger}
          headingBgColor="#0D141B"
          headingTextColor="#FFFFFF"
          customQueryParams={(params) => ({
            ...params,
            token: auth.token,
            search: searchQuery.trim() || undefined,
          })}
          initialPageSize={25}
          setPageCallback={(fn) => (pageSetterRef.current = fn)}
          searchQuery={searchQuery}
        />
      </div>

      <ConfirmDeleteModal
        visible={showConfirm}
        onConfirm={async () => {
          await handleDelete(selectedCustomer?._id);
          setShowConfirm(false);
          setSelectedCustomer(null);
        }}
        onCancel={() => {
          setShowConfirm(false);
          setSelectedCustomer(null);
        }}
        title="Confirm Delete"
        content={`Are you sure you want to reject the customer ? `}
        loading={isDeletingCustomer}
        disabled={isDeletingCustomer}
      />

      <ConfirmSuspendModal
        visible={showSuspendConfirm}
        entityName="Customer"
        onConfirm={async () => {
          const customerId = selectedCustomer?._id;
          setShowSuspendConfirm(false);
          setSelectedCustomer(null);

          if (customerId) {
            await handleSuspend(customerId);
          }
        }}
        onCancel={() => {
          setShowSuspendConfirm(false);
          setSelectedCustomer(null);
        }}
      />

      <ConfirmDeleteModal
        visible={showPermanentSuspendConfirm}
        onConfirm={async () => {
          await handlePermanentlySuspend(selectedCustomer?._id);
          setShowPermanentSuspendConfirm(false);
          setSelectedCustomer(null);
        }}
        onCancel={() => {
          setShowPermanentSuspendConfirm(false);
          setSelectedCustomer(null);
        }}
        title="Confirm Permanent Suspension"
        content={`Are you sure you want to permanently suspend this customer? This will block their login and cancel their active subscription.`}
        loading={false}
        disabled={false}
      />
    </div>
  );
};

export default ManageCustomers;
